import React from 'react';
import { Users, Target, Award, Heart } from 'lucide-react';
import { aboutData, teamData } from '../data/mockData';

export const About = () => {
  return (
    <div className="bg-[rgb(17,17,19)] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4623520/pexels-photo-4623520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgb(17,17,19)] via-[rgba(17,17,19,0.8)] to-[rgb(17,17,19)]" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-6 py-2 bg-[rgba(0,250,99,0.1)] border border-[rgba(0,250,99,0.3)] rounded-full mb-6">
              <span className="text-[rgb(0,250,99)] text-sm font-medium">
                About Onvyn
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {aboutData.title}
            </h1>
            <p className="text-xl text-[rgb(218,218,218)]">
              {aboutData.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section id="story" className="py-24 bg-[rgb(26,28,30)]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 text-lg text-[rgb(218,218,218)] leading-relaxed">
              {aboutData.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-[rgb(17,17,19)]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-[rgb(218,218,218)] max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {[
              {
                icon: Target,
                title: 'Clarity',
                description: 'We simplify complex healthcare concepts into clear, compelling messages'
              },
              {
                icon: Heart,
                title: 'Integrity',
                description: 'Ethical, compliant communication that builds genuine trust'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'High standards in strategy, creativity, and execution'
              },
              {
                icon: Users,
                title: 'Partnership',
                description: 'Collaborative relationships focused on long-term success'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:-translate-y-2 transition-all duration-300 hover:border-[rgb(0,250,99)] group relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-[rgb(0,250,99)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="w-14 h-14 rounded-xl bg-[rgba(0,250,99,0.1)] flex items-center justify-center mb-6 group-hover:bg-[rgb(0,250,99)] transition-colors duration-300">
                    <Icon
                      size={28}
                      className="text-[rgb(0,250,99)] group-hover:text-[rgb(17,17,19)] transition-colors duration-300"
                    />
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[rgb(161,161,170)] text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-[rgb(26,28,30)]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-[rgb(218,218,218)] max-w-3xl mx-auto">
              Healthcare marketing experts dedicated to your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamData.map((member) => (
              <div
                key={member.id}
                className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 hover:border-[rgb(0,250,99)] group"
              >
                <div className="relative h-80 bg-[rgb(38,40,42)] overflow-hidden">
                  <img
                    src={`https://images.unsplash.com/photo-1759884247160-27b8465544b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHx0ZWFtJTIwY29sbGFib3JhdGlvbiUyMHN0cmF0ZWd5fGVufDB8fHx8MTc3MTQzODk2NXww&ixlib=rb-4.1.0&q=85`}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgb(17,17,19)] to-transparent" />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {member.name}
                  </h3>
                  <p className="text-[rgb(0,250,99)] text-sm font-medium mb-3">
                    {member.position}
                  </p>
                  <p className="text-[rgb(161,161,170)] text-sm">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)] max-w-6xl mx-auto">
            <img
              src="https://images.pexels.com/photos/5327580/pexels-photo-5327580.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Healthcare professionals"
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
